// =============================================================================
//  MOTOR RELACIONAL — "A Bússola do Encontro"  ("O Coração de Todas as Coisas")
//  Lê a dança entre duas bússolas. Espelho para a conversa, não um veredito.
//  Uso:  import { gerarEncontro } from './relacionalEngine';
//        const enc = gerarEncontro(resultA, resultB, { nomeA:'Ana', nomeB:'Léo' });
//  resultA / resultB são objetos do computeResult (têm roots{k:{pct}}, dominant...).
// =============================================================================
import C from '../data/encontroContent';

const ROOTS = ['peregrino','guardiao','amante','alquimista'];
const HOT = 55, COLD = 45;  // Thresholds mais inclusivos para gerar mais conteúdo
const fill = (tpl, map) => tpl.replace(/\{(\w+)\}/g, (_, k) => map[k] != null ? map[k] : `{${k}}`);
const domRootOf = (r) => r.dominant.type === 'root' ? r.dominant.key
                       : r.dominant.type === 'transition' ? r.dominant.roots[0]
                       : Object.keys(r.roots).sort((a,b)=>r.roots[b].pct-r.roots[a].pct)[0];

export function gerarEncontro(a, b, opts = {}) {
  if (!a || !b || !a.roots || !b.roots) return null;
  const nomeA = (opts.nomeA || 'Pessoa 1').trim();
  const nomeB = (opts.nomeB || 'Pessoa 2').trim();
  const pa = Object.fromEntries(ROOTS.map(k => [k, a.roots[k].pct]));
  const pb = Object.fromEntries(ROOTS.map(k => [k, b.roots[k].pct]));

  // Estações dominantes de cada um
  const dA = domRootOf(a), dB = domRootOf(b);
  
  // Estações mais forte e mais fraca do casal (média)
  const mediaRoots = ROOTS.map(k => ({ k, m: (pa[k] + pb[k]) / 2 }));
  const maisForte = mediaRoots.sort((x, y) => y.m - x.m)[0].k;
  const maisFraco = mediaRoots.sort((x, y) => x.m - y.m)[0].k;

  // 1) ENCONTROS — ambos fortes na mesma raiz OU ambos acima de 50% e próximos
  const encontros = ROOTS.filter(k => {
    const ambosFortes = pa[k] >= HOT && pb[k] >= HOT;
    const ambosAcima50 = pa[k] >= 50 && pb[k] >= 50 && Math.abs(pa[k] - pb[k]) <= 15;
    return ambosFortes || ambosAcima50;
  }).map(k => ({ root: k, nome: C.ROOT_NOME[k], texto: C.ENCONTRO_RAIZ[k] }));

  // 2) COMPLEMENTOS — um forte, o outro silencioso OU diferença significativa (>20%)
  const complementos = [];
  for (const k of ROOTS) {
    let forte = null, fraco = null;
    const diff = Math.abs(pa[k] - pb[k]);
    
    // Caso clássico: um forte, outro fraco
    if (pa[k] >= HOT && pb[k] < COLD) { forte = nomeA; fraco = nomeB; }
    else if (pb[k] >= HOT && pa[k] < COLD) { forte = nomeB; fraco = nomeA; }
    // Caso alternativo: diferença significativa (>20 pontos)
    else if (diff >= 20) {
      if (pa[k] > pb[k]) { forte = nomeA; fraco = nomeB; }
      else { forte = nomeB; fraco = nomeA; }
    }
    
    if (forte) complementos.push({ root: k, nome: C.ROOT_NOME[k], forte, fraco,
      texto: fill(C.COMPLEMENTO_RAIZ[k], { forte, fraco }) });
  }

  // 3) TENSÃO DO CASAL — raízes dominantes diferentes
  let tensao = null;
  if (dA !== dB) {
    const key = [dA, dB].sort().join('__');
    tensao = { 
      pair: [dA, dB], 
      dominanteA: dA, 
      dominanteB: dB, 
      texto: C.TENSAO_CASAL[key] || '',
      risco: C.RISCOS_CASAL[key] || ''
    };
  }

  // 4) CUIDADO RECÍPROCO — sempre gera para os dois, baseado nas estações mais fortes de cada um
  const cuidado = [];
  const maisForteA = ROOTS.reduce((max, k) => pa[k] > pa[max] ? k : max, ROOTS[0]);
  const maisForteB = ROOTS.reduce((max, k) => pb[k] > pb[max] ? k : max, ROOTS[0]);
  
  cuidado.push({
    de: nomeA,
    para: nomeB,
    root: maisForteA,
    texto: `${nomeA} pode ${C.CUIDADO_PISTA[maisForteA]}.`
  });
  
  cuidado.push({
    de: nomeB,
    para: nomeA,
    root: maisForteB,
    texto: `${nomeB} pode ${C.CUIDADO_PISTA[maisForteB]}.`
  });

  // 5) PONTOS FORTES DO CASAL (baseado na estação mais forte em média)
  const pontosFortes = {
    estacao: maisForte,
    nome: C.ROOT_NOME[maisForte],
    texto: C.PONTOS_FORTES_CASAL[maisForte]
  };

  // 6) O QUE DESENVOLVER JUNTOS (baseado na estação mais fraca em média)
  const desenvolver = {
    estacao: maisFraco,
    nome: C.ROOT_NOME[maisFraco],
    texto: C.DESENVOLVER_JUNTOS[maisFraco]
  };

  // 7) PRÁTICA PARA O CASAL (baseado na estação que mais precisam cultivar)
  const pratica = {
    estacao: maisFraco,
    nome: C.ROOT_NOME[maisFraco],
    texto: C.PRATICAS_CASAL[maisFraco]
  };

  // 8) LINGUAGEM DO AMOR DO CASAL (baseado na estação mais forte)
  const linguagem = {
    estacao: maisForte,
    ...C.LINGUAGEM_CASAL[maisForte]
  };

  // 9) PERGUNTA PROVOCATIVA (baseada no ponto cego - gera reflexão)
  const pergunta = C.PERGUNTAS_CASAL[maisFraco];

  // 10) ESPELHO DO CASAL (como um reflete o outro - baseado na estação mais forte)
  const espelho = C.ESPELHO_CASAL[maisForte];

  // 11) ENSINO MÚTUO (o que um pode ensinar ao outro - baseado na maior diferença)
  const maiorDiff = ROOTS.map(k => ({ k, diff: Math.abs(pa[k] - pb[k]) })).sort((a, b) => b.diff - a.diff)[0];
  const quemEMaisForte = pa[maiorDiff.k] > pb[maiorDiff.k] ? nomeA : nomeB;
  const quemEMaisFraco = pa[maiorDiff.k] > pb[maiorDiff.k] ? nomeB : nomeA;
  const ensinoMutuo = fill(C.ENSINO_MUTUO[maiorDiff.k] || '', { A: quemEMaisForte, B: quemEMaisFraco });

  // 12) INSIGHT DO LIVRO (opcional - baseado na estação mais forte)
  const insightLivro = C.INSIGHT_LIVRO[maisForte];

  // 13) CAPÍTULO DO LIVRO — pela estação que ambos têm mais silenciosa
  const capitulos = [];
  if (C.CAP_ENCONTRO[maisFraco]) capitulos.push({ ...C.CAP_ENCONTRO[maisFraco],
    porque: `Para cultivarem juntos a estação que está mais quieta entre vocês: o ${C.ROOT_NOME[maisFraco]}.` });
  capitulos.push({ ...C.CAP_VINCULO, porque: 'Para celebrar o encontro de vocês.' });

  // 14) FRASE-SEMENTE — pelo padrão predominante
  let padrao = 'equilibrio';
  if (encontros.length >= 2) padrao = 'encontro';
  else if (complementos.length >= 2) padrao = 'complemento';
  else if (tensao) padrao = 'tensao';
  const semente = C.SEMENTE_ENCONTRO[padrao];

  // 15) SELO — a confiabilidade do encontro é a menor das duas leituras
  const ordem = { alta: 3, media: 2, baixa: 1 };
  const fa = a.confidence?.flag, fb = b.confidence?.flag;
  const selo = (fa && fb) ? (ordem[fa] <= ordem[fb] ? fa : fb) : null;

  // Panorama numérico (para a bússola dupla)
  const panorama = ROOTS.map(k => ({ root: k, nome: C.ROOT_NOME[k], a: pa[k], b: pb[k] }));

  return { 
    nomeA, nomeB, 
    encontros, complementos, tensao, cuidado, 
    pontosFortes, desenvolver, pratica, linguagem,
    pergunta, espelho, ensinoMutuo, insightLivro,
    capitulos, semente, selo, padrao, panorama,
    domA: dA, domB: dB 
  };
}
export default gerarEncontro;
